import { Divider, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { useFormikContext } from 'formik'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import React, { useEffect } from 'react'
import Box from '@mui/material/Box'

import { useNotificationContext } from 'shared/context/Notifications'
import RadioField from 'shared/components/RadioButton'
import InputField from 'shared/components/InputField'

import { CustomerAddressesType, DeliveryType, FormikDeliveryAddressType, ReflinkDataType } from '../../types'

interface Props {
   handleIsValid: (isValid: boolean) => void

   availableAddresses: CustomerAddressesType
   locationCountry: string
   deliveryType: DeliveryType
   extraComments: Nullable<string>
   deliveryAddress: PropType<ReflinkDataType, 'deliveryAddress'>
}

export default function AddressData({
   handleIsValid,
   extraComments,
   availableAddresses,
   locationCountry,
   deliveryAddress,
   deliveryType,
}: Props) {
   // const { data: countriesData } = useCountriesQuery()
   const { t } = useTranslation(['TRANSLATION', 'CLIENT_CART'])
   const { values, isValid, setValues } = useFormikContext<FormikDeliveryAddressType>()
   // const [countryOptions, setCountryOptions] = useState([
   //    {
   //       name: '',
   //       value: '',
   //    },
   // ])

   // useEffect(() => {
   //    if (countriesData && values) {
   //       setCountryOptions(
   //          Object.keys(countriesData).map((item) => ({
   //             name: countriesData[item].name,
   //             value: item,
   //          }))
   //       )
   //    }
   // }, [countriesData, values])

   useEffect(() => {
      handleIsValid(isValid)
   }, [isValid])

   useEffect(() => {
      setValues({
         deliveryType: deliveryType || 'PICK_UP',
         deliveryAddress: {
            street: deliveryAddress?.street || '',
            city: deliveryAddress?.city || '',
            country: deliveryAddress?.country || locationCountry || 'Poland',
            zipCode: deliveryAddress?.zipCode || '',
            houseNumber: deliveryAddress?.houseNumber || '',
         },
         extraComments: extraComments || null,
      })
   }, [])

   useEffect(() => {
      if (values.deliveryType === 'PICK_UP') {
         setValues({
            ...values,
            deliveryAddress: {
               street: '',
               city: '',
               country: locationCountry || 'Poland',
               zipCode: '',
               houseNumber: '',
            },
            extraComments: values.extraComments,
         })
      }
   }, [values.deliveryType])

   return (
      <Box sx={{ width: '100%', mt: 2 }}>
         <Box
            sx={{
               display: 'flex',
               width: '100%',
               flexDirection: { lg: 'row', md: 'column', xs: 'column' },
               justifyContent: 'space-between',
               gap: '20px',
            }}
         >
            <Box
               sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  m: '0 auto',
                  gap: 2,
               }}
            >
               <Typography variant="h6" color="primary">
                  {t('CLIENT_CART:addressData.deliveryOptions')}
               </Typography>
               <RadioField
                  name="deliveryType"
                  options={[
                     { label: t('CLIENT_CART:addressData.personalPickUp'), value: 'PICK_UP' },
                     {
                        label: t('CLIENT_CART:addressData.shipping'),
                        value: 'ADDRESS',
                     },
                  ]}
               />
               {/* <SelectField
                     inputDisabled={values.deliveryType === 'PICK_UP'}
                     name="country"
                     inputVariant="outlined"
                     label={t('TRANSLATION:formFields.country')}
                     options={countryOptions}
                  /> */}
               {availableAddresses.length > 0 && (
                  <FormControl fullWidth variant="outlined">
                     <InputLabel>{t('CLIENT_CART:addressData.selectAddress')}</InputLabel>
                     <Select
                        disabled={values.deliveryType === 'PICK_UP'}
                        fullWidth
                        onChange={(e) => {
                           setValues({
                              ...values,
                              deliveryAddress: {
                                 zipCode: e.target.value!.split('/')[0],
                                 city: e.target.value!.split('/')[1],
                                 street: e.target.value!.split('/')[2],
                                 houseNumber: e.target.value!.split('/')[3],
                                 country: 'Poland',
                              },
                           })
                        }}
                        value={`${values.deliveryAddress.zipCode} ${values.deliveryAddress.city}, ${values.deliveryAddress.street} ${values.deliveryAddress.houseNumber}`}
                        renderValue={() =>
                           `${values.deliveryAddress.zipCode} ${values.deliveryAddress.city}, ${values.deliveryAddress.street} ${values.deliveryAddress.houseNumber}`
                        }
                        label={t('CLIENT_CART:addressData.selectAddress')}
                        variant="outlined"
                     >
                        {availableAddresses.map((address) => (
                           <MenuItem
                              value={`${address.zipCode}/${address.city}/${address.street}/${address.houseNumber}`}
                           >{`${address.zipCode} ${address.city}, ${address.street} ${address.houseNumber}`}</MenuItem>
                        ))}
                     </Select>
                  </FormControl>
               )}
               <Box
                  sx={{
                     display: 'flex',
                     width: '100%',
                     gap: 1,
                     flexDirection: { md: 'row', sm: 'column', xs: 'column' },
                  }}
               >
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: { md: 'row', sm: 'column', xs: 'column' },
                        flex: 1,
                        gap: 2,
                     }}
                  >
                     <InputField
                        fullWidth
                        name="deliveryAddress.city"
                        disabled={values.deliveryType === 'PICK_UP'}
                        label={t('TRANSLATION:formFields.city')}
                     />
                     <InputField
                        fullWidth
                        name="deliveryAddress.zipCode"
                        disabled={values.deliveryType === 'PICK_UP'}
                        label={t('TRANSLATION:formFields.postCode')}
                     />
                  </Box>
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: { md: 'row', sm: 'column', xs: 'column' },
                        flex: 1,
                        gap: 2,
                     }}
                  >
                     <InputField
                        fullWidth
                        name="deliveryAddress.street"
                        disabled={values.deliveryType === 'PICK_UP'}
                        label={t('TRANSLATION:formFields.street')}
                     />
                     <InputField
                        fullWidth
                        name="deliveryAddress.houseNumber"
                        disabled={values.deliveryType === 'PICK_UP'}
                        label={t('TRANSLATION:formFields.houseNumber')}
                     />
                  </Box>
               </Box>
            </Box>
            <Divider
               flexItem
               orientation="vertical"
               color="primary"
               sx={{
                  borderColor: 'primary.main',
                  // borderWidth: '1px',
                  borderRadius: '25px',
               }}
            />
            <Box
               sx={{
                  flex: 1,
                  display: 'flex',
                  gap: 1.2,
                  flexDirection: 'column',
               }}
            >
               <Typography variant="h6" color="primary">
                  {t('CLIENT_CART:addressData.extraCommentsLabel')}
               </Typography>
               <InputField
                  name="extraComments"
                  placeholder={t('CLIENT_CART:addressData.extraCommentsPlaceholder')}
                  multiline
                  slotProps={{
                     htmlInput: {
                        maxLength: 300,
                     },
                  }}
                  sx={{
                     width: '100%',
                  }}
                  error={values.extraComments?.length === 300}
                  helperText={
                     values.extraComments?.length === 300 ? t('CLIENT_CART:addressData.extraCommentsLengthLimit') : ''
                  }
                  rows={8}
                  fullWidth
                  label=""
               />
            </Box>
         </Box>
      </Box>
   )
}
